import { inject, injectable } from 'tsyringe';
import { LoggerService } from 'app-core/src/services/logger.service';
import { OWPlugin } from '../../../odk-ts';
import { FunctionPropertyNames } from 'app-core/src/types';
import {
  kNameOfApprovedDLCertificates,
  kNameOfDownloadPlugin,
  kNameOfDownloadService,
  kNameOfOwCRNDlUserAgent,
} from 'app-core/src/constants/constants';
import { IDownloadServiceDelegate } from 'app-core/src/interfaces/download-service-delegate.interface';
import { IDownloadService } from 'app-core/src/interfaces/download-service.interface';
import { DownloaderPlugin } from './downloader-plugin.interface';
import { kDITOWWrapper } from 'app-core/src/constants/injectionTokens';
import { IOWWrapper } from 'app-core/src/interfaces/ow-wrappers/ow-wrapper.interface';

// -----------------------------------------------------------------------------
@injectable()
export class OverwolfDownloadService implements IDownloadService {
  private readonly logger = LoggerService.getCategory(kNameOfDownloadService);
  private readonly listeners: Set<IDownloadServiceDelegate>;
  private downloaderPluginInstance: DownloaderPlugin;
  private externalDownloadPath: string;
  private autoRun = false;
  public downloadingUrl: string;
  public isDownloading = false;
  public downloadingAppid: string;

  // ---------------------------------------------------------------------------
  public constructor(
    @inject(kDITOWWrapper)
    private readonly owWrapper: IOWWrapper,
  ) {
    this.listeners = new Set<IDownloadServiceDelegate>();
    this.init();
  }

  // ---------------------------------------------------------------------------
  private triggerListenerEvent(
    eventName: FunctionPropertyNames<IDownloadServiceDelegate>,
    param: any,
  ) {
    if (!eventName) {
      return;
    }

    for (const listener of this.listeners) {
      if (!listener[eventName]) {
        continue;
      }

      listener[eventName](param);
    }
  }

  // ---------------------------------------------------------------------------
  // DelegateProvider
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  addListener(listener: IDownloadServiceDelegate) {
    this.listeners?.add(listener);
  }

  removeListener(listener: IDownloadServiceDelegate) {
    this.listeners?.delete(listener);
  }

  // ---------------------------------------------------------------------------
  // IDownloadService
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  public downloadFile(
    url: string,
    downloadPath: string,
    downloadAppId: string,
    autoRun = true,
  ) {
    this.downloadingAppid = downloadAppId;

    try {
      if (!this.downloaderPluginInstance) {
        throw new Error('downloaderPluginInstance is undefined');
      }

      if (this.isDownloading) {
        throw new Error('Download plugin already downloading');
      }

      this.logger.info(
        `Download File requested, url: ${url}, path: ${downloadPath}`,
      );

      this.downloadingUrl = url;
      this.autoRun = autoRun;
      this.downloaderPluginInstance.downloadFile(
        url,
        downloadPath,
        `${kNameOfOwCRNDlUserAgent} ${this.owWrapper.getVersion()}`,
      );
      this.triggerListenerEvent('onDownloadStart', url);
      this.externalDownloadPath = downloadPath;
    } catch (error) {
      this.logger.error('Error while requesting download file', error);
      this.onDownloadError(error.message);
    }
  }

  // ---------------------------------------------------------------------------
  public cancelDownload() {
    this.logger.info('Download Cancel requested');
    this.downloaderPluginInstance.cancelDownload();
    this.externalDownloadPath = null;
    this.downloadingUrl = null;
  }

  // ---------------------------------------------------------------------------
  public runFileProcess(downloadPath: string): boolean {
    try {
      this.logger.info(`Running file process, File: ${downloadPath}`);
      this.downloaderPluginInstance.ExecuteFileProcess(downloadPath);

      return true;
    } catch (error) {
      this.logger.error(
        `Error Running file process, File: ${downloadPath}`,
        error,
      );

      return false;
    }
  }

  // ---------------------------------------------------------------------------
  private async init() {
    try {
      const plugin = await OWPlugin.create(kNameOfDownloadPlugin);
      if (!plugin.isInitialized || !plugin?.instance) {
        throw new Error('Error obtaining download plugin');
      }

      this.downloaderPluginInstance = plugin?.instance;
      this.downloaderPluginInstance.onDownloadComplete.addListener(
        this.onDownloadComplete.bind(this),
      );

      this.downloaderPluginInstance.onDownloadError.addListener(
        this.onDownloadError.bind(this),
      );

      this.downloaderPluginInstance.onDownloadProgress.addListener(
        this.onDownloadProgress.bind(this),
      );

      this.downloaderPluginInstance.onFileExecuted.addListener(
        this.onFileExecuted.bind(this),
      );
    } catch (error) {
      this.logger.error('Error during download plugin initialization ', error);
    }
  }

  // ---------------------------------------------------------------------------
  private onDownloadComplete(...args) {
    this.isDownloading = false;
    this.logger.info('Download Complete', ...args);
    this.triggerListenerEvent('onDownloadCompleted', args[0]?.url ?? null);
    if (this.autoRun) {
      this.runFileProcess(this.externalDownloadPath);
      this.externalDownloadPath = null;
      this.autoRun = false;
    }
  }

  // ---------------------------------------------------------------------------
  private onDownloadError(...args) {
    this.isDownloading = false;
    this.logger.info('Download error', ...args);
    this.triggerListenerEvent('onDownloadError', args[0]?.reason ?? '');
  }

  // ---------------------------------------------------------------------------
  private onDownloadProgress(...args) {
    let progress;
    try {
      if (!this.isDownloading) {
        this.isDownloading = true;
      }

      progress = args[0].progress;
      this.triggerListenerEvent('onDownloadProgress', progress);
    } catch (error) {}
  }

  // ---------------------------------------------------------------------------
  private onFileExecuted(...args) {
    try {
      const result = args[0].result;
      this.logger.info(`onFileExecuted Result: ${result}`);

      if (!result) {
        this.logger.error(`Error executing file, Reason: ${args[0]?.reason}`);
      }

      this.triggerListenerEvent('onFileExecuted', result);
      this.downloadingUrl = null;
    } catch (error) {}
  }
}
